import React from 'react';
import { PropInfo, CodeExample, StepperProps, StepProps, StepGroupProps } from '@cimpress/react-components';

import StepperCode from '!raw-loader!./stepper';
import StepperDemo from './stepper';
import ComponentDoc from '../../shared/TabbedComponentDoc';

export default function StepperDocs() {
  // stepper proptypes
  const stepperProps: PropInfo<StepperProps>[] = [
    {
      name: 'activeStep',
      type: 'number or string',
      default: '',
      description: `Tells the stepper which step should be active.
        Pass the index of the step which should be active.
        If sub- steps are used, pass the active step in the format <MajorStep>.< SubStep > `,
    },
    {
      name: 'partial',
      type: 'boolean',
      default: 'false',
      description: `Tells the stepper that the currently active step is only partially complete - will display a half-filled circle.
        Avoid using unless you have a compelling UX reason.`,
    },
    {
      name: 'status',
      type: 'string',
      default: 'success',
      description: 'Tells the stepper which validation state to use for the active step',
    },
    {
      name: 'small',
      type: 'boolean',
      default: 'false',
      description:
        'Condenses stepper padding for small steppers. Additional children of Step components will not be shown on small steppers.',
    },
    {
      name: 'vertical',
      type: 'boolean',
      default: 'false',
      description: 'Stepper will be displayed vertically.',
    },
    {
      name: 'children',
      type: 'array of nodes',
      default: '',
      description: (
        <span>
          Children of the stepper should be <code>&lt;Step&gt;</code> elements.
        </span>
      ),
    },
  ];

  // stepGroup PropTypes
  const stepGroupProps: PropInfo<StepGroupProps>[] = [
    {
      name: 'contents',
      type: 'node',
      default: '',
      description: 'Additional information about the step to render below the step indicator',
    },
    {
      name: 'children',
      type: 'node',
      default: '',
      description: (
        <span>
          Children of the StepGroup should be <code>&lt;Step&gt;</code> elements. They will render nested.
        </span>
      ),
    },
    {
      name: 'onClick',
      type: 'function',
      default: '',
      description: (
        <span>
          <p>
            Function to call when a step is clicked on. If no function is provided, steps will not appear clickable.
          </p>
          <p>
            Signature: <code>(index: string, name: string) {'=>'} void</code>
          </p>
          <p>
            <code>index:</code> The index of the clicked step
          </p>
          <p>
            <code>name:</code> the name prop of the clicked step
          </p>
        </span>
      ),
    },
    {
      name: 'name',
      type: 'string',
      default: '',
      description: 'Name for the step.  Provided to the onClick function when it is called.',
    },
    {
      name: 'tip',
      type: 'node',
      default: '',
      description: 'If provided, content for a tooltip to appear when a user hovers over the step',
    },
    {
      name: 'icon',
      type: 'node',
      default: '',
      description: "An element that will replace the step's indicator.",
    },
    {
      name: 'tipDirection',
      type: 'string',
      default: 'top',
      description: 'Direction for the tooltip (see tooltip documentation)',
    },
    {
      name: 'overrideStatus',
      type: 'string',
      default: '',
      description: (
        <div>
          <p>
            Sets the color of the parent-step regardless of whether it's completed, active, etc. This prop is not passed
            to sub-steps. If any sub-steps have an overrideStatus set, the most severe overrideStatus will take
            precedence over this value.
          </p>
          <p>
            Use one of <code>success</code>, <code>warning</code>, <code>danger</code>, or <code>none</code>.
          </p>
        </div>
      ),
    },
  ];

  // step PropTypes
  const stepProps: PropInfo<StepProps>[] = [
    {
      name: 'onClick',
      type: 'function',
      default: '',
      description: (
        <span>
          <p>
            Function to call when a step is clicked on. If no function is provided, steps will not appear clickable.
          </p>
          <p>
            Signature: <code>(index: string, name: string) {'=>'} void</code>
          </p>
          <p>
            <code>index:</code> The index of the clicked step
          </p>
          <p>
            <code>name:</code> the name prop of the clicked step
          </p>
        </span>
      ),
    },
    {
      name: 'name',
      type: 'string',
      default: '',
      description: 'Name for the step.  Provided to the onClick function when it is called.',
    },
    {
      name: 'overrideStatus',
      type: 'string',
      default: '',
      description: (
        <div>
          <p>Sets the color of the step regardless of whether it's completed, active, etc.</p>
          <p>
            Use one of <code>success</code>, <code>warning</code>, <code>danger</code>, or <code>none</code>.
          </p>
        </div>
      ),
    },
    {
      name: 'tip',
      type: 'node',
      default: '',
      description: 'If provided, content for a tooltip to appear when a user hovers over the step',
    },
    {
      name: 'icon',
      type: 'node',
      default: '',
      description: "An element that will replace the step's indicator.",
    },
    {
      name: 'tipDirection',
      type: 'string',
      default: 'top',
      description: 'Direction for the tooltip (see tooltip documentation)',
    },
    {
      name: 'children',
      type: 'node',
      default: '',
      description: 'Additional information about the step to render below the step indicator',
    },
    {
      name: 'overrideStatus',
      type: 'string',
      default: '',
      description: (
        <div>
          <p>
            Sets the color of the parent-step regardless of whether it's completed, active, etc. This prop is not passed
            to sub-steps. If any sub-steps have an overrideStatus set, the most severe overrideStatus will take
            precedence over this value.
          </p>
          <p>
            Use one of <code>success</code>, <code>warning</code>, <code>danger</code>, or <code>none</code>.
          </p>
        </div>
      ),
    },
  ];

  return (
    <div>
      <ComponentDoc name="Stepper" propInfos={stepperProps} />
      <ComponentDoc name="StepGroup" propInfos={stepGroupProps} includeTabs={false} />
      <ComponentDoc name="Step" propInfos={stepProps} includeTabs={false}>
        <StepperDemo />
        <CodeExample code={StepperCode} />
      </ComponentDoc>
    </div>
  );
}
